import { Spinner } from 'evergreen-ui';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import * as routes from 'pages/routes';

import * as companyResource from 'resources/company/hooks';
import * as userResource from 'resources/user/hooks';

import { useLogout } from 'resources/auth/hooks';
import * as userHelpers from 'resources/user/user.helpers';
import useWindowDimensions from 'hooks/useWindowDimensions';
import useCachedTransactionSession from 'hooks/useCachedTransactionSession';
import useTrySignUpWithGoogle from './hooks/useTrySignUpWithGoogle';
import useProcessEmailLink from './hooks/useProcessEmailLink';
import useUserTabs from './hooks/useUserTabs';

import BoxLayout from './BoxLayout';
import MainLayout from './MainLayout';
import MarketingLayout from './MarketingLayout';
import PrivateScope from './PrivateScope';
import UnauthorizedLayout from './UnauthorizedLayout';


import styles from './styles.module.css';

const layoutToComponent = {
  [routes.layout.MAIN]: MainLayout,
  [routes.layout.BOX]: BoxLayout,
  [routes.layout.MARKETING]: MarketingLayout,
  [routes.layout.UNAUTHORIZED]: UnauthorizedLayout,
  [routes.layout.NONE]: ({ children }) => children,
};

const scopeToComponent = {
  [routes.scope.PRIVATE]: ({ children, isEmailVerificationToast }) => (
    <PrivateScope isEmailVerificationToast={isEmailVerificationToast}>
      {children}
    </PrivateScope>
  ),
  [routes.scope.PUBLIC]: ({ children }) => children,
  [routes.scope.NONE]: ({ children }) => children,
};

const PageConfig = ({ session, children }) => {
  const router = useRouter();
  const { mutateAsync: logout } = useLogout();
  const { isDesktopWidth } = useWindowDimensions();
  const { storeSession } = useCachedTransactionSession();

  const [isLoading, setIsLoading] = useState(false);

  const { isLoading: isUserLoading, data: user } = userResource.useCurrentUser();
  const { data: company } = companyResource.useCompany(user?.company_id);
  const { lastMenuTabsScreens } = useUserTabs({ user, company });

  const isRegisteredActiveUser = userHelpers.isRegistered(user) && !userHelpers.isDeactivated(user);

  useEffect(() => {
    if (router.query.aiTransactionId && [routes.path.signIn, routes.path.signUp].includes(router.pathname)) {
      storeSession(router.query.aiTransactionId);
    }
  }, [router.pathname, router.query.aiTransactionId, storeSession]);


  useTrySignUpWithGoogle({ session, setIsLoading });

  // process an email link (Invite, Reset Password, Upgrade Now)
  useProcessEmailLink({ setIsLoading });

  // logout a user if he is deactivated or if he tries to access the /logout/ page
  useEffect(async () => {
    if (router.asPath === '/logout/') {
      await logout();
      return null;
    }
  }, [router.asPath]);

  if (isUserLoading || isLoading) {
    return <div className={styles.loaderContainer}><Spinner /></div>;
  }

  // redirect a user to the page he was trying to access before the Sign In page
  if (user && router.query.redirect) {
    router.push(router.query.redirect);
    return null;
  }
  
  // allow an invited user to access only the Sign Up page for invited users
  if (userHelpers.isInvited(user) && !router.asPath.includes(routes.path.signUpInvitee)) {
    router.push(routes.path.signUpInvitee);
    return null;
  }

  // redirect a user either to Home or Sign In if they are not authorized to access the page
  const page = routes.configurations(user, company).find((r) => r.path === router.route);
  if (!page) {
    const isSignedIn = !!user;
    if (isSignedIn) {
      router.push(routes.path.home);
    } else {
      router.push({
        pathname: routes.path.signIn,
        // use window.location.pathname instead of router.asPath
        // as for the path '/client-profile/736eebbb-701b-4ded-a41a-bee93e7ffc81/'
        // router.asPath equals to '/client-profile/[id]'
        query: { redirect: `${window.location.pathname}${window.location.search}` }
      });
    }
    
    return null;
  }

  const Layout = isDesktopWidth && page.desktopLayout
    ? layoutToComponent[page.desktopLayout]
    : layoutToComponent[page.layout];
  const Scope = scopeToComponent[page.scope];
  const isEmailVerificationToast = !page.hideEmailVerificationToast;


  if (user && page.scope === routes.scope.PRIVATE) {
    // redirect a deactivated user to the Logout page
    if (userHelpers.isDeactivated(user)) {
      router.push('/logout/');
      return null;
    }

    // redirect the signed up user to the Setup Account Info page
    if (userHelpers.isManager(user) && !user.phone_number && !router.asPath.includes(routes.path.setupAccountInfo)) {
      router.push(routes.path.setupAccountInfo);
      return null;
    }
  }

  // redirect a user to the Home page if he is already registered
  if (page.scope === routes.scope.PUBLIC && isRegisteredActiveUser) {
    router.push(routes.path.home);
    return null;
  }

  return (
    <Scope
      isEmailVerificationToast={isEmailVerificationToast}
    >
      <Layout
        lastMenuTabsScreens={lastMenuTabsScreens}
        pageContentOffset={page.pageContentOffset}
      >
        {children}
      </Layout>
    </Scope>
  );
};

PageConfig.propTypes = {
  children: PropTypes.node.isRequired,
  session: PropTypes.shape(),
};

PageConfig.defaultProps = {
  session: null,
};

export default PageConfig;
